import React, { useState } from "react";
import SEO from "../components/SEO";
import { tours } from "../data/tours";

// The real catalogue, by name. This was a list of tour TYPES — and three of
// them (Beach & Sailing, Wildlife Safari, Photography Tours) were things this
// operator does not run, so the first thing an enquiry asked for was a trip
// that does not exist. Knowing which itinerary somebody means is more useful
// than knowing which category, and it cannot drift from what is on offer.
const TOUR_OPTIONS = [
  "Select a tour...",
  ...tours.map((t: { title: string }) => t.title),
  "Other / custom itinerary",
];

interface FormFields {
  name: string;
  email: string;
  phone: string;
  tour: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  tour?: string;
  message?: string;
}

function validate(fields: FormFields): FormErrors {
  const errors: FormErrors = {};

  if (!fields.name.trim()) {
    errors.name = "Full name is required.";
  }

  if (!fields.email.trim()) {
    errors.email = "Email address is required.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) {
    errors.email = "Please enter a valid email address.";
  }

  if (fields.phone && !/^[+\d\s\-().]{7,20}$/.test(fields.phone)) {
    errors.phone = "Please enter a valid phone number.";
  }

  if (!fields.tour || fields.tour === TOUR_OPTIONS[0]) {
    errors.tour = "Please select a desired tour.";
  }

  if (!fields.message.trim()) {
    errors.message = "Message is required.";
  } else if (fields.message.trim().length < 10) {
    errors.message = "Message must be at least 10 characters.";
  }

  return errors;
}

const FORMSPREE_ENDPOINT = "https://formspree.io/f/xdkozqdp";

export default function ContactPage() {
  const [fields, setFields] = useState<FormFields>({
    name: "",
    email: "",
    phone: "",
    tour: TOUR_OPTIONS[0],
    message: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<keyof FormFields, boolean>>>({});
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
    if (touched[name as keyof FormFields]) {
      setErrors((prev) => ({
        ...prev,
        ...validate({ ...fields, [name]: value }),
        // clear error if now valid
        [name]: validate({ ...fields, [name]: value })[name as keyof FormErrors],
      }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, ...validate(fields) }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationErrors = validate(fields);
    setErrors(validationErrors);
    setTouched({ name: true, email: true, phone: true, tour: true, message: true });

    if (Object.keys(validationErrors).length > 0) return;

    setStatus("submitting");

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: fields.name,
          email: fields.email,
          phone: fields.phone || "Not provided",
          tour: fields.tour,
          message: fields.message,
        }),
      });

      if (response.ok) {
        setStatus("success");
        setFields({ name: "", email: "", phone: "", tour: TOUR_OPTIONS[0], message: "" });
        setTouched({});
        setErrors({});
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const inputBase =
    "w-full px-4 py-3 rounded-xl border text-gray-800 placeholder-gray-400 text-sm transition-all duration-200 focus:outline-none focus:ring-2";
  const inputNormal = `${inputBase} border-gray-200 bg-white focus:border-primary-500 focus:ring-primary-100`;
  const inputError = `${inputBase} border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-100`;

  return (
    <>
      <SEO
        title="Contact & Book | D, Trio Trek"
        description="Plan a Skardu, Baltistan or Hunza expedition with D, Trio Trek. Message us on WhatsApp or send your dates."
        url="https://dtriotrek.com/contact"
      />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRjMC0yLjIgMS44LTQgNC00czQgMS44IDQgNC0xLjggNC00IDQtNC0xLjgtNC00ek0xNiAxNGMwLTIuMiAxLjgtNCA0LTRzNCAxLjggNCA0LTEuOCA0LTQgNC00LTEuOC00LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] bg-repeat" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block bg-white/20 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-5 backdrop-blur-sm">
            Skardu · Islamabad
          </span>
          <h1 className="font-display text-4xl md:text-6xl font-normal tracking-tight text-white mb-5">
            Contact &amp; Book Now
          </h1>
          <p className="text-primary-200 text-lg max-w-2xl mx-auto">
            Send us your dates and what you want to see in Gilgit-Baltistan. WhatsApp is fastest.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-y bg-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">

            {/* Contact Info Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white p-8 shadow-sm border border-gray-100">
                <h2 className="font-display text-xl font-bold text-gray-900 mb-6">Get in Touch</h2>
                <ul className="space-y-5">
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Our Office</p>
                      <p className="text-gray-500 text-sm mt-0.5">Skardu & Islamabad, Pakistan</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Email Us</p>
                      <a href="mailto:dtriotrek@gmail.com" className="text-primary-600 hover:text-primary-700 text-sm mt-0.5 transition-colors">
                        dtriotrek@gmail.com
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Call Us</p>
                      <a href="tel:+923102329666" className="text-primary-600 hover:text-primary-700 text-sm mt-0.5 transition-colors">
                        +92 310 2329666
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">WhatsApp</p>
                      <a
                        href="https://wa.me/923102329666"
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary-600 hover:text-primary-700 text-sm mt-0.5 transition-colors"
                      >
                        +92 310 2329666
                      </a>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Why Choose Us */}
              <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl p-8 text-white">
                <h3 className="font-display text-lg font-bold mb-4">Why Book With Us?</h3>
                <ul className="space-y-3 text-sm">
                  {[
                    "A local Gilgit-Baltistan team, not an agent elsewhere",
                    "Private transport and 4x4 jeeps, never a shared coach",
                    "Itineraries built around your Skardu flight",
                    "Guides who know when Deosai and the passes open",
                    "Written cancellation terms, stated up front",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-primary-200 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-primary-100">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-white shadow-sm border border-gray-100 p-8 md:p-10">

                {/* Success State */}
                {status === "success" ? (
                  <div className="flex flex-col items-center justify-center text-center py-12 animate-fade-in">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                      <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="font-display text-2xl font-bold text-gray-900 mb-3">
                      Message Sent!
                    </h3>
                    <p className="text-gray-500 max-w-md mb-8">
                      Thank you for reaching out! Our travel experts will review your inquiry and get back to you within 24 hours.
                    </p>
                    <button
                      onClick={() => setStatus("idle")}
                      className="btn-primary"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="mb-8">
                      <h2 className="font-display text-2xl font-bold text-gray-900 mb-2">
                        Send Us a Message
                      </h2>
                      <p className="text-gray-500 text-sm">
                        Fill in the details below and we'll tailor the perfect adventure for you.
                      </p>
                    </div>

                    {/* Error Banner */}
                    {status === "error" && (
                      <div className="mb-6 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Something went wrong. Please try again or email us directly at dtriotrek@gmail.com.
                      </div>
                    )}

                    <form
                      onSubmit={handleSubmit}
                      noValidate
                      className="space-y-5"
                    >
                      {/* Name + Email */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
                            Full Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="name"
                            name="name"
                            type="text"
                            autoComplete="name"
                            placeholder="Jane Smith"
                            value={fields.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={touched.name && errors.name ? inputError : inputNormal}
                          />
                          {touched.name && errors.name && (
                            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              {errors.name}
                            </p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                            Email Address <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            placeholder="jane@example.com"
                            value={fields.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={touched.email && errors.email ? inputError : inputNormal}
                          />
                          {touched.email && errors.email && (
                            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              {errors.email}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Phone + Tour */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                            Phone Number <span className="text-gray-400 font-normal">(optional)</span>
                          </label>
                          <input
                            id="phone"
                            name="phone"
                            type="tel"
                            autoComplete="tel"
                            placeholder="+92 300 0000000"
                            value={fields.phone}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={touched.phone && errors.phone ? inputError : inputNormal}
                          />
                          {touched.phone && errors.phone && (
                            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              {errors.phone}
                            </p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="tour" className="block text-sm font-medium text-gray-700 mb-1.5">
                            Desired Tour <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <select
                              id="tour"
                              name="tour"
                              value={fields.tour}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`${touched.tour && errors.tour ? inputError : inputNormal} appearance-none pr-10 cursor-pointer`}
                            >
                              {TOUR_OPTIONS.map((opt) => (
                                <option key={opt} value={opt} disabled={opt === TOUR_OPTIONS[0]}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>
                          {touched.tour && errors.tour && (
                            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              {errors.tour}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Message */}
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">
                          Message <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          rows={5}
                          placeholder="Tell us about your ideal trip — dates, group size, special requirements…"
                          value={fields.message}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`${touched.message && errors.message ? inputError : inputNormal} resize-none`}
                        />
                        {touched.message && errors.message && (
                          <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            {errors.message}
                          </p>
                        )}
                        <p className="mt-1.5 text-xs text-gray-400 text-right">
                          {fields.message.length} / 1000
                        </p>
                      </div>

                      {/* Submit */}
                      <div className="pt-2">
                        <button
                          type="submit"
                          disabled={status === "submitting"}
                          className="w-full btn-primary flex items-center justify-center gap-2 py-4 text-base disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {status === "submitting" ? (
                            <>
                              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                              Sending…
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                              </svg>
                              Send Inquiry &amp; Book Now
                            </>
                          )}
                        </button>
                        <p className="text-center text-xs text-gray-400 mt-3">
                          We respect your privacy. Your details will never be shared with third parties.
                        </p>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}